import type { DeliveryMarker } from "../utils/tracking-map-state";
import type { DeliveryTrackingMarkerLabels } from "../utils/tracking-marker-element";

type Props = {
  markers: DeliveryMarker[];
  labels: DeliveryTrackingMarkerLabels;
  className?: string;
};

// =============================================================================
// Issue #81 — Self-identifying Delivery Tracking markers and provisional legend
//
// Compact, horizontal pill list that explains the visible marker types only.
// Origin, destination, and (when present) the Rider marker render a small
// preview that mirrors the on-map icon so the legend stays visually
// consistent with the markers themselves. The Rider chip switches between
// live and stale copy without re-mounting the whole legend.
// =============================================================================

export function DeliveryTrackingLegend({ markers, labels, className }: Props) {
  if (markers.length === 0) {
    return null;
  }

  const visibleKinds = new Set(markers.map((marker) => marker.kind));
  const rider = markers.find((marker) => marker.kind === "rider");
  const riderLabel = rider?.freshness === "stale" ? labels.riderStale : labels.riderLive;
  const riderFreshness = rider?.freshness;

  return (
    <ul
      aria-label="Delivery tracking legend"
      data-testid="delivery-tracking-legend"
      className={"flex flex-wrap items-center gap-2 text-xs text-foreground " + (className ?? "")}
    >
      {visibleKinds.has("origin") ? <LegendItem kind="origin" label={labels.origin} /> : null}
      {visibleKinds.has("destination") ? (
        <LegendItem kind="destination" label={labels.destination} />
      ) : null}
      {visibleKinds.has("rider") ? (
        <LegendItem kind="rider" freshness={riderFreshness} label={riderLabel} />
      ) : null}
    </ul>
  );
}

type LegendItemProps = {
  kind: "origin" | "destination" | "rider";
  label: string;
  freshness?: "live" | "stale";
};

function LegendItem({ kind, label, freshness }: LegendItemProps) {
  return (
    <li
      className="flex items-center gap-1.5 rounded-full border bg-background/90 px-2 py-1 shadow-sm backdrop-blur"
      data-marker-kind={kind}
      data-marker-freshness={kind === "rider" && freshness ? freshness : undefined}
    >
      <MarkerGlyph
        kind={kind}
        freshness={freshness}
        className="delivery-marker delivery-marker--legend"
      />
      <span>{label}</span>
    </li>
  );
}

function MarkerGlyph({
  kind,
  freshness,
  className,
}: {
  kind: "origin" | "destination" | "rider";
  freshness?: "live" | "stale";
  className?: string;
}) {
  const staleClass = kind === "rider" && freshness === "stale" ? "delivery-marker--stale" : "";
  const combined = ["inline-flex", staleClass, className ?? ""].filter(Boolean).join(" ");
  return (
    <span
      className={combined}
      data-marker-kind={kind}
      data-marker-freshness={kind === "rider" && freshness ? freshness : undefined}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" width={20} height={20} focusable="false">
        {kind === "origin" ? (
          <>
            <path d="M7 8h10l-1 12H8L7 8z" />
            <path d="M9.5 8V6.2a2.5 2.5 0 0 1 5 0V8" fill="none" strokeLinecap="round" />
          </>
        ) : kind === "destination" ? (
          <>
            <path d="M5 11.5 12 5l7 6.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6.5 10.5V19h11v-8.5" />
            <path d="M10.5 19v-5h3v5" fill="none" />
          </>
        ) : (
          <>
            <path d="M12 4 6 18l6-3.5L18 18 12 4z" />
            <circle cx="12" cy="4" r="1.4" />
          </>
        )}
      </svg>
    </span>
  );
}
